import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Separator } from '@monksflow/monks-ui';

type ChatTranscriptMessageRole = 'user' | 'assistant' | 'system' | 'tool';

export interface ChatTranscriptMessage {
  role: ChatTranscriptMessageRole;
  content: string;
  speakerName?: string | null;
  createdAt?: string;
  isHandoff?: boolean;
}

export interface ChatTranscriptPreviewProps {
  title?: string;
  messages: ChatTranscriptMessage[];
  emptyState?: ReactNode;
  /**
   * Lays the whole transcript out at full height instead of inside a scroll
   * container, so a headless PDF capture gets every message rather than just
   * the first viewport (the `/download` print page).
   */
  exporting?: boolean;
}

function formatTimestamp(iso: string | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function ChatTranscriptPreview({
  title,
  messages,
  emptyState,
  exporting = false,
}: ChatTranscriptPreviewProps) {
  const { t } = useTranslation('common');
  const visible = messages.filter((m) => m.role === 'user' || m.role === 'assistant');
  const containerClassName = exporting
    ? 'flex flex-col gap-4 p-6'
    : 'flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-6';

  if (visible.length === 0) {
    return (
      <div className={containerClassName}>
        {title && <h2 className="text-base font-medium text-foreground">{title}</h2>}
        {emptyState ?? (
          <p className="text-sm text-muted-foreground">
            {t('ui.chatTranscriptPreview.noMessagesInThisConversation')}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className={containerClassName}>
      {title && <h2 className="text-base font-medium text-foreground">{title}</h2>}
      <div className="flex flex-col gap-6">
        {visible.map((message, index) => {
          if (message.isHandoff) {
            return (
              <HandoffSeparator
                key={`handoff-${index.toString()}`}
                label={message.speakerName ?? message.content}
              />
            );
          }

          const isUser = message.role === 'user';
          const timestamp = formatTimestamp(message.createdAt);
          const speakerLabel = isUser
            ? t('ui.chatTranscriptPreview.you')
            : (message.speakerName ?? t('ui.chatTranscriptPreview.assistant'));

          return (
            <div
              key={`msg-${index.toString()}`}
              className={`flex flex-col gap-2 ${isUser ? 'items-end pl-10' : 'items-start pr-10'}`}
            >
              <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                <span>{speakerLabel}</span>
                {timestamp && <span>· {timestamp}</span>}
              </div>
              <div
                className={`max-w-[80%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                  isUser
                    ? 'rounded-tr-sm bg-accent text-accent-foreground'
                    : 'rounded-tl-sm bg-gray-100 text-gray-950'
                }`}
              >
                {message.content}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function HandoffSeparator({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 py-2">
      <Separator className="flex-1" />
      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        → {label}
      </span>
      <Separator className="flex-1" />
    </div>
  );
}
